"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SupportTicketEventSchema = exports.SupportTicketChangeSchema = exports.SupportCreateTicketRequestSchema = exports.SupportAddReferenceRequestSchema = exports.SupportAssignRequestSchema = exports.SupportReplyRequestSchema = exports.AdminSupportHistoryResponseSchema = exports.AdminSupportHistoryEntrySchema = exports.AdminSupportHistoryMessageSchema = exports.AdminSupportHistoryMarkerSchema = exports.AdminSupportHistoryQuerySchema = exports.AdminSupportTicketDetailSchema = exports.AdminSupportMessageSchema = exports.AdminSupportTicketListResponseSchema = exports.AdminSupportTicketItemSchema = exports.AdminSupportUserSchema = exports.AdminSupportReferenceSchema = exports.AdminSupportOperationSchema = exports.AdminSupportTicketListQuerySchema = void 0;
const zod_1 = require("zod");
const auth_1 = require("../auth");
const locale_1 = require("../../locale");
const wallet_1 = require("../wallet");
const support_1 = require("../support");
const common_1 = require("./common");
exports.AdminSupportTicketListQuerySchema = common_1.CursorQuerySchema.extend({
    cursor: zod_1.z.uuid().optional(),
    status: zod_1.z.enum(['open', 'pending', 'closed', 'all']).default('open'),
    assigned: zod_1.z.enum(['all', 'me', 'unassigned']).default('all'),
    userId: zod_1.z.uuid().optional(),
    /** Both or neither: "tickets about this operation" (spec 2026-10-28 §5.5). */
    referenceKind: support_1.SupportReferenceKindSchema.optional(),
    referenceId: zod_1.z.uuid().optional(),
}).refine((q) => (q.referenceKind === undefined) === (q.referenceId === undefined), {
    message: 'referenceKind and referenceId go together',
    path: ['referenceId'],
});
/** What a reference row shows: the operation as it is now. Null when the operation row is gone. */
exports.AdminSupportOperationSchema = zod_1.z.object({
    amount: wallet_1.AtomicAmountSchema,
    decimals: zod_1.z.number().int().min(0),
    symbol: zod_1.z.string(),
    chain: zod_1.z.string(),
    status: zod_1.z.string(),
    createdAt: zod_1.z.iso.datetime(),
    txHash: zod_1.z.string().nullable(),
    exchangerOrderId: zod_1.z.number().int().nullable(),
    toAmount: wallet_1.AtomicAmountSchema.nullable(),
    toDecimals: zod_1.z.number().int().min(0).nullable(),
    toCurrency: zod_1.z.string().nullable(),
});
exports.AdminSupportReferenceSchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    kind: support_1.SupportReferenceKindSchema,
    referenceId: zod_1.z.uuid(),
    addedBy: zod_1.z.enum(['user', 'staff']),
    createdAt: zod_1.z.iso.datetime(),
    operation: exports.AdminSupportOperationSchema.nullable(),
});
exports.AdminSupportUserSchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    publicId: zod_1.z.string(),
    /** BigInt in the database; a string on the wire. Null only for merged rows, which never own a ticket. */
    telegramId: zod_1.z.string().nullable(),
    username: zod_1.z.string().nullable(),
    firstName: zod_1.z.string().nullable(),
    status: auth_1.UserStatusSchema,
});
exports.AdminSupportTicketItemSchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    number: zod_1.z.number().int(),
    status: support_1.SupportTicketStatusSchema,
    category: support_1.SupportCategorySchema.nullable(),
    openedFrom: support_1.SupportTicketOriginSchema,
    user: exports.AdminSupportUserSchema,
    assignedTo: common_1.StaffRefSchema.nullable(),
    lastUserMessageAt: zod_1.z.iso.datetime().nullable(),
    lastStaffMessageAt: zod_1.z.iso.datetime().nullable(),
    createdAt: zod_1.z.iso.datetime(),
    updatedAt: zod_1.z.iso.datetime(),
    /** First 120 characters of the latest user message, or `[photo]` / `[file]`, or empty. */
    preview: zod_1.z.string(),
    references: zod_1.z.array(exports.AdminSupportReferenceSchema),
});
exports.AdminSupportTicketListResponseSchema = (0, common_1.pageSchema)(exports.AdminSupportTicketItemSchema).extend({
    /** Global count of `open` tickets, regardless of filters: the sidebar badge. */
    openCount: zod_1.z.number().int().nonnegative(),
});
exports.AdminSupportMessageSchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    author: zod_1.z.enum(['user', 'staff', 'system']),
    staff: common_1.StaffRefSchema.nullable(),
    /** User text or caption, a staff reply, or a system code (`closed`, `reopened`). */
    text: zod_1.z.string().nullable(),
    attachments: zod_1.z.array(support_1.SupportAttachmentSchema),
    deliveryStatus: zod_1.z.enum(['queued', 'sent', 'failed']).nullable(),
    deliveryError: zod_1.z.string().nullable(),
    deliveredAt: zod_1.z.iso.datetime().nullable(),
    createdAt: zod_1.z.iso.datetime(),
});
exports.AdminSupportTicketDetailSchema = exports.AdminSupportTicketItemSchema.extend({
    user: exports.AdminSupportUserSchema.extend({ email: zod_1.z.string().nullable(), locale: zod_1.z.enum(locale_1.LOCALES) }),
    messages: zod_1.z.array(exports.AdminSupportMessageSchema),
    firstStaffReplyAt: zod_1.z.iso.datetime().nullable(),
    closedAt: zod_1.z.iso.datetime().nullable(),
    closedBy: common_1.StaffRefSchema.nullable(),
    reopenedCount: zod_1.z.number().int().nonnegative(),
});
/** `GET /admin/support/users/:userId/history` query: a page of the user's whole support history. */
exports.AdminSupportHistoryQuerySchema = zod_1.z.object({
    /** Only entries strictly before this instant (the previous page's `nextBefore`). */
    before: zod_1.z.iso.datetime().optional(),
    limit: zod_1.z.coerce.number().int().min(1).max(200).default(100),
});
/** A ticket boundary in the history: where a ticket was opened (with its operations), closed or reopened. */
exports.AdminSupportHistoryMarkerSchema = zod_1.z.object({
    kind: zod_1.z.literal('marker'),
    event: zod_1.z.enum(['opened', 'closed', 'reopened']),
    ticketId: zod_1.z.uuid(),
    number: zod_1.z.number().int(),
    at: zod_1.z.iso.datetime(),
    category: support_1.SupportCategorySchema.nullable(),
    openedFrom: support_1.SupportTicketOriginSchema,
    /** Active references of the ticket; filled on `opened`, empty otherwise. */
    references: zod_1.z.array(exports.AdminSupportReferenceSchema),
    /** Who closed it, on the ticket's latest `closed` marker; null otherwise. */
    by: common_1.StaffRefSchema.nullable(),
});
exports.AdminSupportHistoryMessageSchema = zod_1.z.object({
    kind: zod_1.z.literal('message'),
    ticketId: zod_1.z.uuid(),
    number: zod_1.z.number().int(),
    message: exports.AdminSupportMessageSchema,
});
exports.AdminSupportHistoryEntrySchema = zod_1.z.discriminatedUnion('kind', [
    exports.AdminSupportHistoryMarkerSchema,
    exports.AdminSupportHistoryMessageSchema,
]);
/** Entries oldest first; `nextBefore` is the cursor for the earlier page, null when none. */
exports.AdminSupportHistoryResponseSchema = zod_1.z.object({
    entries: zod_1.z.array(exports.AdminSupportHistoryEntrySchema),
    nextBefore: zod_1.z.iso.datetime().nullable(),
});
exports.SupportReplyRequestSchema = zod_1.z.object({
    text: zod_1.z.string().trim().min(1).max(support_1.SUPPORT_REPLY_MAX_LENGTH),
});
exports.SupportAssignRequestSchema = zod_1.z.object({ staffId: zod_1.z.uuid().nullable() });
exports.SupportAddReferenceRequestSchema = zod_1.z.object({
    kind: support_1.SupportReferenceKindSchema,
    referenceId: zod_1.z.uuid(),
});
exports.SupportCreateTicketRequestSchema = zod_1.z.object({
    userId: zod_1.z.uuid(),
    text: zod_1.z.string().trim().min(1).max(support_1.SUPPORT_REPLY_MAX_LENGTH),
    reference: exports.SupportAddReferenceRequestSchema.optional(),
});
/** What changed on a ticket; the admin re-fetches the detail, so the kind is informational. */
exports.SupportTicketChangeSchema = zod_1.z.enum([
    'message',
    'delivery',
    'status',
    'assignment',
    'category',
    'references',
]);
/** One `ticket` event on the admin support stream (spec 2026-10-28 live updates §3). */
exports.SupportTicketEventSchema = zod_1.z.object({
    ticketId: zod_1.z.uuid(),
    change: exports.SupportTicketChangeSchema,
});
//# sourceMappingURL=support.js.map